import CarPageBasicComponent from './car-page-basic-component';
import analytics from '../../analytics';
import { validatePhone } from '../../tools';

/**
 * CarPageContactModal controls the contact modal box.
 */
class CarPageContactModal extends CarPageBasicComponent {
  state = { contactDialog: false };

  /**
   * Initializes the component: renders, binds, starts observers etc...
   * @param {[CarPageApp]} carPageApp optional, if doesn't exist, it's created. (The CarPageApp component is a singleton)
   */
  constructor(carPageApp) {
    super(carPageApp);
    this.element = document.querySelector('#carcard-contact-modal');

    if (this.element) {
      this.initState();
      this.render();
      this.bind(['observeIndependentChange']);
      this.initControls();
    }
  }

  /**
   * initializes the component's control elements. Currently only the close button to close the dialog.
   * Also starts an observer that detects if the dialog closes independently, and passes the info down.
   */
  initControls() {
    this.closeButton = this.element.querySelector('nav.contentcard-titlecontrol');
    this.closeButton.addEventListener('click', this.toggleDialogState.bind(this));

    this.callRequestInput = this.element.querySelector('input[type="tel"]');
    this.talkBackErrorMessage = this.element.querySelector('#talk-back-error-message');
    this.talkBackErrorMessageText = this.talkBackErrorMessage?.querySelector('p');
    this.talkBackErrorMessageDefaultText = this.talkBackErrorMessageText?.textContent || '';
    this.submitButton = this.element.querySelector('#carcard-contact-modal-submit');
    this.submitButton?.addEventListener('click', this.handleSubmitClick.bind(this));

    this.initIndependetChangesObserver();
  }

  /**
   * Validate phone number before submit and push analytics only for valid submits.
   *
   * @param {Event} event click event
   */
  handleSubmitClick(event) {
    if (!this.#isPhoneNumberValid()) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    this.#resetPhoneValidationError();
    this.pushCallbackDataLayer();
  }

  /**
   * Validate contact modal phone input with shared tools validation.
   *
   * @returns {boolean}
   */
  #isPhoneNumberValid() {
    const value = this.callRequestInput.value?.trim() || '';
    const isValid = value.length > 8 && validatePhone(value);

    if (!isValid && this.talkBackErrorMessage && this.talkBackErrorMessageText) {
      this.talkBackErrorMessageText.textContent = 'Puhelinnumero on virheellinen.';
      this.talkBackErrorMessage.hidden = false;
    } else {
      this.#resetPhoneValidationError();
    }

    return isValid;
  }

  /**
   * Reset talk-back error message to original text and hide it.
   */
  #resetPhoneValidationError() {
    if (this.talkBackErrorMessage && this.talkBackErrorMessageText) {
      this.talkBackErrorMessage.hidden = true;
      this.talkBackErrorMessageText.textContent = this.talkBackErrorMessageDefaultText;
    }
  }

  /**
   * The elevated container has a clickable overlay that can close the dialog.
   * This starts an observer to watch the modal for changes, in case it closes independently.
   */
  initIndependetChangesObserver() {
    // Create an observer instance linked to the callback function
    const observer = new MutationObserver(this.observeIndependentChange);

    // Start observing the target node for configured mutations
    observer.observe(this.element, { attributes: true, childList: false, subtree: false });
  }

  /**
   * MutationObserver callback https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver
   * @param  {MutationRecord[]} mutations https://developer.mozilla.org/en-US/docs/Web/API/MutationRecord
   */
  observeIndependentChange(mutations) {
    mutations.forEach(mutation => {
      if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
        if (this.elementVisibility !== this.state.contactDialog) {
          this.app.updateState('dialogs.contactDialog', this.elementVisibility);
        }
      }
    });
  }

  /**
   * Toggles the state dialogState and passes it to controller.
   */
  toggleDialogState() {
    this.app.updateState('dialogs.contactDialog', !this.state.contactDialog);
  }

  /**
   * Isolate from the entire app state only the part that concerns the component,
   * and only observe changes in that sub state.
   * @param  {object} state receives the app state from controller app
   * @return {object} the isolated sub state, for example state.calculator.calculatorType
   */
  filterState(state) {
    // eslint-disable-next-line no-prototype-builtins
    if (typeof state === 'object' && state.hasOwnProperty('dialogs')) {
      return { ...state.dialogs };
    }
    return {};
  }

  /**
   * getter, reads the visibility value of the actual element. (This is a bad practise generally,
   * but needed to bridge the non-compatible element with the new state logic)
   * @return {boolean} true = visible, false = not visible
   */
  get elementVisibility() {
    if (!this.element) return false;
    return this.element.classList.contains('visible');
  }

  /**
   * Initializes the component's state, in this case only the visibility of the dialog, and passes it to app state.
   *
   */
  pushCallbackDataLayer() {
    const car = this.app.getState().carData;
    const urlParams = new URLSearchParams(window.location.search);

    analytics.pushToDataLayer({
      event: 'car_callback_request',
      car_page_type: urlParams.get('hedindrive') === '1' ? 'hedin-drive' : 'normal',
      car_register_id: car.license,
      car_brand: car.brand_name,
      car_model: car.model_name,
      car_construction_year: car.year,
      car_price: car.price?.price,
      car_type: car.car_category_name,
      car_outlet: car.basicinfo?.location?.name,
      car_fuelType: car.propulsion?.name,
      car_mileage: car.mileage,
      car_transmission: car.gear?.name,
      car_emission: car.technical?.co2emission,
      click_text: car.car_name,
      click_url: window.location.href,
      is_hedin_drive: car.hedinDriveData ? true : false,
      is_beely: car.beely_link ? true : false,
      page_origin: urlParams.get('ai-result') === '1' ? 'ai_car_search' : '',
      car_vehicleType: car.basicinfo?.bodyType
    });

    analytics.pushToDataLayer({
      event: 'conversion',
      conversion_id: '1771744796955',
      conversion_name: 'Auto - Soittopyyntö jätetty',
      conversion_value: '100'
    });
  }

  /**
   * Updates the visible component when asked. In this case calls the visibility
   * toggle of the external component.
   */
  render() {
    if (this.element && this.state.contactDialog !== this.elementVisibility) {
      this.element.toggleVisibility(this.state.contactDialog);
    }
  }
}

export default CarPageContactModal;
